import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { CheckCircle, XCircle, Eye, Download, Loader2 } from 'lucide-react';
import { api } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

export default function ResponsesPage() {
  const campaignId = useParams().id;
  const queryClient = useQueryClient();
  const [selectedResponse, setSelectedResponse] = useState<Record<string, any> | null>(null);

  const { data: responses, isLoading, error } = useQuery({
    queryKey: ['campaign-responses', campaignId],
    queryFn: () => campaignId ? api.getCampaignResponses(campaignId) : Promise.resolve([]),
    enabled: !!campaignId,
  });

  const approveMutation = useMutation({
    mutationFn: (responseId: string) => api.updateResponseStatus(responseId, 'approved'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaign-responses', campaignId] });
    },
  });

  const rejectMutation = useMutation({
    mutationFn: (responseId: string) => api.updateResponseStatus(responseId, 'rejected'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaign-responses', campaignId] });
    },
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <Card className="p-6 text-center text-muted-foreground">
        Failed to load responses. Please try again later.
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Responses</h1>
          <p className="text-muted-foreground">Review and manage campaign responses</p>
        </div>
        <Button variant="outline">
          <Download className="w-4 h-4 mr-2" />
          Export CSV
        </Button>
      </div>

      {!responses || responses.length === 0 ? (
        <Card className="p-12 text-center">
          <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
            <Eye className="w-8 h-8 text-muted-foreground" />
          </div>
          <h3 className="text-lg font-semibold mb-2">No responses yet</h3>
          <p className="text-muted-foreground">
            Responses will appear here once surveyors submit data.
          </p>
        </Card>
      ) : (
        <div className="bg-card rounded-lg border border-border">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Surveyor</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Location</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Status</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Date</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {responses.map((response: any) => (
                  <tr key={response.id} className="hover:bg-muted/50">
                    <td className="py-4 px-4 font-medium">
                      {response.surveyor?.full_name || 'Anonymous'}
                    </td>
                    <td className="py-4 px-4 text-muted-foreground">
                      {response.target_location?.name || 'N/A'}
                    </td>
                    <td className="py-4 px-4">
                      <Badge variant="secondary" className={getStatusColor(response.status)}>
                        {response.status}
                      </Badge>
                    </td>
                    <td className="py-4 px-4 text-muted-foreground">
                      {new Date(response.created_at).toLocaleDateString()}
                    </td>
                    <td className="py-4 px-4 text-right">
                      <div className="flex items-center justify-end space-x-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setSelectedResponse(response)}
                          title="View"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        {response.status === 'pending' && (
                          <>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-green-600"
                              onClick={() => approveMutation.mutate(response.id)}
                              disabled={approveMutation.isPending}
                              title="Approve"
                            >
                              <CheckCircle className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-red-600"
                              onClick={() => rejectMutation.mutate(response.id)}
                              disabled={rejectMutation.isPending}
                              title="Reject"
                            >
                              <XCircle className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Dialog open={!!selectedResponse} onOpenChange={() => setSelectedResponse(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Response Details</DialogTitle>
          </DialogHeader>
          {selectedResponse && (
            <div className="space-y-4 max-h-[60vh] overflow-y-auto">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Surveyor:</span>
                  <p className="font-medium">
                    {(selectedResponse as any).surveyor?.full_name || 'Anonymous'}
                  </p>
                </div>
                <div>
                  <span className="text-muted-foreground">Location:</span>
                  <p className="font-medium">
                    {(selectedResponse as any).target_location?.name || 'N/A'}
                  </p>
                </div>
                <div>
                  <span className="text-muted-foreground">Submitted:</span>
                  <p className="font-medium">
                    {new Date((selectedResponse as any).created_at).toLocaleString()}
                  </p>
                </div>
                <div>
                  <span className="text-muted-foreground">Status:</span>
                  <Badge variant="secondary" className={getStatusColor((selectedResponse as any).status)}>
                    {(selectedResponse as any).status}
                  </Badge>
                </div>
              </div>
              <div className="border-t pt-4">
                <h4 className="font-medium mb-2">Response Data</h4>
                <pre className="bg-muted p-4 rounded-lg text-sm overflow-x-auto">
                  {JSON.stringify((selectedResponse as any).survey_data, null, 2)}
                </pre>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
